/**
 * Pure filename/path helpers for the "Build manifest" page (docs/168-ADMIN-
 * MANIFEST-BUILDER.md v0) — copied verbatim from
 * `apps/admin-web/src/lib/manifestFile.ts` (mirrors `apps/admin/ui/manifestFile.ts`).
 * Duplicated (not shared cross-app) rather than moved into `@equipped/core`,
 * matching docs/325's own note that `ui/manifestFile.ts` hasn't been
 * core-ified yet; kept byte-identical so a future core-ify pass can delete
 * every copy in favor of one shared module without behavior drift.
 */

/** Last path segment of an R2 key (e.g. "guitar/lesson-1.md" → "lesson-1.md"). */
export function basename(key: string): string {
  const parts = key.split("/");
  return parts[parts.length - 1] ?? key;
}

/** `basename` with its extension stripped (e.g. "lesson-1.md" → "lesson-1"). */
export function basenameNoExt(key: string): string {
  const b = basename(key);
  const i = b.lastIndexOf(".");
  return i > 0 ? b.slice(0, i) : b;
}

/** A readable default lesson title guessed from a filename ("lesson-1" → "Lesson 1"). */
export function titleFromFilename(key: string): string {
  const words = basenameNoExt(key)
    .replace(/[-_]+/g, " ")
    .trim();
  if (!words) return basenameNoExt(key);
  return words.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The value to store in `manifest.lessons[].file` for a browsed R2 object —
 * see `apps/admin/ui/manifestFile.ts`'s own doc comment for the full
 * reasoning (resolved relative to `<packId>/manifest.json`).
 */
export function lessonFileForManifest(packId: string, fileKey: string): string {
  const clean = fileKey.replace(/^\/+/, "");
  const segments = clean.split("/").filter((s) => s.length > 0);
  if (segments.length > 1 && segments[0] === packId) {
    return segments.slice(1).join("/");
  }
  return `../${clean}`;
}

/**
 * True when a browsed file's top-level R2 folder matches the pack id — the
 * case `lessonFileForManifest` resolves with a plain relative path. A
 * mismatch still produces a resolvable URL (via `../`) but won't pass the
 * server's gated-token check for THIS pack — surfaced as a light warning,
 * not a hard block.
 */
export function fileMatchesPackFolder(packId: string, fileKey: string): boolean {
  const clean = fileKey.replace(/^\/+/, "");
  const segments = clean.split("/").filter((s) => s.length > 0);
  return segments.length > 1 && segments[0] === packId;
}

/** Breadcrumb segments for a browse `prefix` (e.g. "guitar/basics/" → ["guitar", "basics"]). */
export function prefixSegments(prefix: string): string[] {
  return prefix.split("/").filter((s) => s.length > 0);
}

/** The full prefix string for the breadcrumb segment at `index` (0-based, inclusive). */
export function prefixUpTo(prefix: string, index: number): string {
  const segs = prefixSegments(prefix);
  return `${segs.slice(0, index + 1).join("/")}/`;
}

/** Display name for a folder/file row: its key with the current browse `prefix` stripped. */
export function stripPrefix(key: string, prefix: string): string {
  return key.startsWith(prefix) ? key.slice(prefix.length) : key;
}
